import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, App, Popconfirm, Typography, Tooltip, Drawer, Grid } from "antd";
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined, QuestionCircleOutlined, FileOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdvanceTransactionList,
  deleteAdvanceTransaction,
} from "../advanceTransactionThunks";
import {
  setSelectedAdvanceTransaction,
  setIsEditAdvanceTransaction,
} from "../advanceTransactionSlice";
import dayjs from "dayjs";
import { formatDateOnly, getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";
import { useTxLock } from "../../../hooks/useTxLock";
const { Title } = Typography;

const AdvanceTransactionList = ({ onAddNew }) => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const transactions = useSelector((s) => s.advanceTransaction.transactions);
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const isAdminUser = useIsAdminUser();

  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    const access = getAccessType("advance_transaction_entry");
    access_type_set(access);
  }, []);

  const handleDelete = async (tran_id) => {
    try {
      await dispatch(deleteAdvanceTransaction(tran_id)).unwrap();
      modal.success({ title: "Advance transaction deleted successfully" });
      dispatch(fetchAdvanceTransactionList());
    } catch (err) {
      modal.warning({ title: err.message || "Delete failed" });
    }
  };
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const handleEdit = (record) => {
    dispatch(setSelectedAdvanceTransaction(record));
    dispatch(setIsEditAdvanceTransaction(true));
    onAddNew();
  };

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    {
      title: "Date",
      dataIndex: "tran_date",
      render: (d) => formatDateOnly(d),
    },
    { title: "Customer/Supplier", dataIndex: "acc_name" },
    { title: "Tran Type", dataIndex: "tran_type" },
    { title: "Acc Type", dataIndex: "acc_type" },
    { title: "Cash/Bank Account", dataIndex: "tran_acc_name" },
    {
      title: "Amount",
      dataIndex: "tran_amount",
      render: (v) => Number(v || 0).toFixed(2),
    },
    { title: "Description", dataIndex: "narration" },
    { title: "Created By", dataIndex: "full_name" },
 {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.tran_date);

    return (
      <>
        <Tooltip title="View Receipt">
          <Button
            type="link"
            icon={<FileOutlined />}
            onClick={() => navigate(`/accounts/advance-transaction/receipt/${record.tran_id}`)}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            onClick={() => {
              if (txLocked) return;

              handleEdit(record);
            }}
            icon={<EditOutlined />}
            disabled={!(isAdminUser || canEdit) || txLocked}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Popconfirm
            title="Are you sure you want to delete this sale?"
            okText="Yes, Delete"
            cancelText="Cancel"
            disabled={!(isAdminUser || canDelete) || txLocked}
            onConfirm={() => {
              if (txLocked) return;

              handleDelete(record.tran_id);
            }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={!(isAdminUser || canDelete) || txLocked}
            />
          </Popconfirm>
        </Tooltip>
      </>
    );
  },
}
  ];

  useEffect(() => {
    dispatch(fetchAdvanceTransactionList());
  }, [dispatch]);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
          marginBottom: "12px",
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="advance-transactions-back-button"
        />
        <Title level={2} style={{ margin: 0, wordBreak: "break-word" }}>
          Advance Transactions
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      </div>
      <Row justify="end" style={{ marginBottom: 32, marginTop: 32 }}>
        <Col xs={24} md={5} sm="auto">
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                block
                onClick={() => {
                  dispatch(setSelectedAdvanceTransaction(null));
                  dispatch(setIsEditAdvanceTransaction(false));
                  onAddNew();
                }}
                data-testid="btn-add-advance-transaction"
                style={{ width: "100%" }}
              >
                Add New Advance Transaction
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={transactions}
        rowKey="tran_id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: transactions.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
      />
      <Drawer
        title="Advance Transaction Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Advance Transactions
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Advance Transaction module is used to record advance payments
            received from customers or paid to suppliers within the ERP system.
            These transactions help maintain accurate accounting and balance tracking.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View advance transaction records</li>
              <li>Use <strong>Add New Advance Transaction</strong> to create entries</li>
              <li>Edit transaction details when required</li>
              <li>Delete transactions if permitted</li>
              <li>Track customer and supplier advances</li>
              <li>Manage cash and bank transaction entries</li>
              <li>Monitor transaction amounts and narration details</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Advance transactions affect accounting balances immediately.</li>
              <li>Cash and bank account selection should be verified carefully.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for auditing and reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AdvanceTransactionList;
